import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Grid,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const TRUNCATE_SELECT_SX = {
  "& .MuiSelect-select": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

// A dropdown whose selected value truncates (no overflow), with a tooltip
// showing the full label on hover.
const SelectField = ({ label, value, options, onChange }) => (
  <Tooltip title={value ?? ""} placement={"top"}>
    <TextField
      select
      fullWidth
      variant={"filled"}
      label={label}
      InputLabelProps={{ shrink: true }}
      value={value ?? ""}
      onChange={onChange}
      sx={TRUNCATE_SELECT_SX}
    >
      <MenuItem value={""}>{"—"}</MenuItem>
      {options.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  </Tooltip>
);

// Construction / Project-Wrap pricing inputs for the GL Pricing tab. Project type
// and insured interest are the two values that drive the GL engine factors:
// insured interest scales the technical build-up (Prem/Ops + PCO), and project
// type x insured interest feeds the IRPM peculiarities line. Stored on the
// submission program. The class/payroll recommendation these also inform lives on
// the GL Exposures tab.
const ProjectWrapInputs = () => {
  const dispatch = useDispatch();
  const program = useSelector(programSelectors.getProgram);
  const { data: cfg } = staticDataService.useConfigQuery(
    "construction_rating_config"
  );
  const projectTypes = cfg?.projectTypes ?? [];
  const insuredInterests = cfg?.insuredInterests ?? [];
  const [open, setOpen] = useState(false);

  const projectType = program?.projectType ?? null;
  const insuredInterest = program?.insuredInterest ?? null;
  const onUpdate = (values) => dispatch(programActions.updateProgram(values));

  const summary = [projectType, insuredInterest].filter(Boolean).join(" · ");

  return (
    <Stack spacing={0}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        onClick={() => setOpen((v) => !v)}
        sx={{
          cursor: "pointer",
          px: 2,
          py: 1.25,
          borderRadius: 1,
          backgroundColor: "grey.100",
        }}
      >
        <Typography
          fontSize={"0.875rem"}
          fontWeight={700}
          color={"text.secondary"}
        >
          {"Construction / Project-Wrap"}
        </Typography>
        <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
          {summary && (
            <Typography
              fontSize={"0.875rem"}
              fontWeight={600}
              color={"text.secondary"}
            >
              {summary}
            </Typography>
          )}
          {open ? (
            <ExpandLessIcon fontSize={"small"} />
          ) : (
            <ExpandMoreIcon fontSize={"small"} />
          )}
        </Stack>
      </Stack>
      {open && (
        <Stack spacing={1.5} sx={{ pt: 1.5, pb: 1, px: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <SelectField
                label={"Project Type"}
                value={projectType}
                options={projectTypes}
                onChange={(e) =>
                  onUpdate({
                    projectType: e.target.value || null,
                    projectTypeManual: true,
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <SelectField
                label={"Insured Interest"}
                value={insuredInterest}
                options={insuredInterests}
                onChange={(e) =>
                  onUpdate({
                    insuredInterest: e.target.value || null,
                    insuredInterestManual: true,
                  })
                }
              />
            </Grid>
          </Grid>
        </Stack>
      )}
    </Stack>
  );
};

export default ProjectWrapInputs;
